import { FC, useEffect, useState } from 'react'
import SecondaryButton from '@pages/Admin/CourseSettings/components/SecondaryButton/SecondaryButton'
import SelectCourseCard from './components/SelectCourseCard/SelectCourseCard'
import { useGetSettings } from '@pages/CoursesCatalog/tabs/CatalogCoursesTab/hooks/getPlatformSettings'
import { getDiscount } from '@pages/CoursesCatalog/modals/CourseEnrollmentModal/utils/tariff'
import FormItem from 'antd/es/form/FormItem'
import { ICustomCourse } from '../../../SetCourseClassDirectionModal/SetCourseModalBody'
import { parseAndValidateAddedCourses } from './utils/parseAddedCourses'
import sclasses from '../../../SetCourseClassDirectionModal/index.module.css'
import classes from './index.module.css'
import cn from 'classnames'
import { ICourseEnrollmentItem } from './components/SelectCourseCard/types'

interface SelectAdditionalCoursesProps {
  userCourses?: number[]
  isFirstFree: boolean
  currentCourseId: number
  coursesState: { value?: ICustomCourse[]; loading: boolean }

  value?: string | null
  onChange?: (value: string | null) => void
}

const SelectAdditionalCourses: FC<SelectAdditionalCoursesProps> = ({
  userCourses = [],
  isFirstFree,
  currentCourseId,
  coursesState: { value: courses = [], loading: isLoading },

  value: defValue,
  onChange,
}) => {
  const { getSettings } = useGetSettings()
  const discountsUnit = getSettings('extraCoursesDiscountUnit')
  const extraCoursesDiscounts = JSON.parse(
    getSettings('extraCoursesDiscounts') ?? 'null',
  )

  const [addedCourses, setAddedCourses] = useState<ICourseEnrollmentItem[]>(
    parseAndValidateAddedCourses(defValue ?? null),
  )

  useEffect(() => {
    setAddedCourses((prev) =>
      prev?.filter((course) => !userCourses?.includes(course?.courseId!)),
    )
  }, [userCourses?.length])

  useEffect(() => {
    onChange?.(JSON.stringify(addedCourses))
  }, [addedCourses, onChange])

  const onClickAdd = () => {
    setAddedCourses((prev) => [
      ...prev,
      {
        courseId: null,
        tariffId: null,
        enrollDate: null,
        value: null,
      },
    ])
  }

  const onRemove = (index: number) => {
    setAddedCourses((prev) => prev?.filter((_, i) => i !== index))
  }

  const onChangeItem = (
    index: number,
    key: keyof ICourseEnrollmentItem,
    value: number | string | boolean | null | object,
  ) => {
    setAddedCourses(
      (prev) =>
        prev.map((item, i) =>
          i === index ? { ...item, [key]: value } : item,
        ) ?? [],
    )
  }

  const lastCourse = addedCourses?.[addedCourses.length - 1]

  const canAddCourse =
    addedCourses?.length < 5 &&
    lastCourse?.courseId !== null &&
    lastCourse?.tariffId !== null

  const getAddButtonString = () => {
    const discount = getDiscount(
      isFirstFree,
      addedCourses?.length,
      addedCourses.map((item) => ({
        ...item,
        course: (courses.find((course) => course.id === item.courseId) ?? null) as ICourse | null,
        withDemo: false,
      })),
      extraCoursesDiscounts,
      discountsUnit ?? '',
    )

    if (!discount) return 'Добавить курс'

    const nextDiscount = discount?.discount ?? 0
    const stringDiscount =
      discountsUnit === 'percent'
        ? `${nextDiscount}%`
        : `${nextDiscount} рублей`
    return `Добавить курс со скидкой ${stringDiscount}`
  }

  return (
    <div className={classes.wrapper}>
      {!!addedCourses?.length && (
        <div className={classes.courseList}>
          <div className={classes.title}>Дополнительные курсы</div>
          {addedCourses?.map((order, index) => {
            const discount = getDiscount(
              isFirstFree,
              index,
              addedCourses as any,
              extraCoursesDiscounts,
              discountsUnit ?? '',
            )

            return (
              <SelectCourseCard
                key={index}
                isLoading={isLoading}
                order={order}
                allCourses={courses as ICourse[]}
                courses={(courses as ICourse[])
                  ?.filter((course) => course?.id !== currentCourseId)
                  ?.filter(
                    (item) =>
                      !addedCourses?.some(
                        (selected) =>
                          selected?.courseId === item?.id &&
                          order?.courseId !== item?.id,
                      ),
                  )}
                onChange={(key, value) => onChangeItem(index, key, value)}
                onDismiss={() => onRemove(index)}
                promoCode={discount ?? undefined}
              />
            )
          })}
        </div>
      )}
      {canAddCourse && (
        <SecondaryButton
          className={cn(sclasses.tabButtonActive, classes.addCourseButton)}
          onClick={onClickAdd}
        >
          {getAddButtonString()}
        </SecondaryButton>
      )}
    </div>
  )
}

type AdditionalCoursesFieldProps = Omit<
  SelectAdditionalCoursesProps,
  'value' | 'onChange' | 'addedCourses' | 'setAddedCourses'
>

const AdditionalCoursesField: FC<AdditionalCoursesFieldProps> = (props) => {
  const { checkSettings } = useGetSettings()
  const extraCoursesAllowed = checkSettings('isAllowedExtraCoursesPurchase')

  return extraCoursesAllowed ? (
    <FormItem name="additional_courses" noStyle>
      <SelectAdditionalCourses {...props} />
    </FormItem>
  ) : null
}

export default SelectAdditionalCourses
export { AdditionalCoursesField }
